/**
 * ChatInput
 *
 * Auto-resizing textarea with send button and attachment menu.
 * Handles Enter to send, Shift+Enter for new line, clipboard paste.
 */

import { useRef, useEffect, useState } from 'react';
import AttachmentMenu from './AttachmentMenu';
import VoiceRecorder from './VoiceRecorder';
import { isSupportedImageFile } from '@/services/photoStorageService';

export default function ChatInput({
  value,
  onChange,
  onSend,
  onSendMedia,
  onSendVoice,
  sending = false,
  placeholder = 'Type a message...'
}) {
  const textareaRef = useRef(null);
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 150) + 'px';
    }
  }, [value]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (value.trim() && !sending) onSend();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (value.trim() && !sending) onSend();
  };

  // Clipboard paste for images
  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      const file = item.getAsFile();
      if (file && isSupportedImageFile(file)) {
        e.preventDefault();
        onSendMedia?.(file, 'image');
        return;
      }
    }
  };

  // Drop handler for files
  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;

    const type = isSupportedImageFile(file) ? 'image' : 'file';
    onSendMedia?.(file, type);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Voice recording mode
  if (isRecordingVoice) {
    return (
      <VoiceRecorder
        onSend={(blob, duration) => {
          onSendVoice?.(blob, duration);
          setIsRecordingVoice(false);
        }}
        onCancel={() => setIsRecordingVoice(false)}
      />
    );
  }

  return (
    <div
      className="border-t border-base-300 bg-base-100 p-3"
      onDrop={handleDrop}
      onDragOver={handleDragOver}
    >
      <form onSubmit={handleSubmit} className="flex items-end gap-2">
        {/* Attachment menu */}
        <AttachmentMenu
          onSelectImage={(file) => onSendMedia?.(file, 'image')}
          onSelectFile={(file) => onSendMedia?.(file, 'file')}
          onStartVoice={() => setIsRecordingVoice(true)}
        />

        {/* Text input */}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={placeholder}
          className="textarea textarea-bordered flex-1 resize-none min-h-[40px] max-h-[150px] text-sm leading-5 py-2"
          rows={1}
          disabled={sending}
        />

        {/* Send button */}
        <button
          type="submit"
          className="btn btn-primary btn-sm"
          disabled={!value.trim() || sending}
        >
          {sending ? (
            <span className="loading loading-spinner loading-xs" />
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          )}
        </button>
      </form>
    </div>
  );
}
